type WebkitFullscreenVideo = HTMLVideoElement & {
  webkitSupportsFullscreen?: boolean;
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

/** Element Shaka uses for `requestFullscreen` (defaults to the video container). */
export function isFullscreenActive(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.fullscreenElement) return true;
  const video = document.querySelector('video');
  if (video && 'webkitDisplayingFullscreen' in video) {
    return Boolean((video as WebkitFullscreenVideo).webkitDisplayingFullscreen);
  }
  return false;
}

function resolveWebkitVideo(element: HTMLElement): WebkitFullscreenVideo | null {
  const video = element.querySelector('video') ?? (element instanceof HTMLVideoElement ? element : null);
  if (!video || !('webkitEnterFullscreen' in video)) return null;
  return video as WebkitFullscreenVideo;
}

async function toggleStandardFullscreen(element: HTMLElement): Promise<void> {
  if (document.fullscreenElement) {
    await document.exitFullscreen();
    return;
  }
  if (document.pictureInPictureElement) {
    await document.exitPictureInPicture();
  }
  await element.requestFullscreen({ navigationUI: 'hide' });
}

function toggleWebkitVideoFullscreen(video: WebkitFullscreenVideo): void {
  if (!video.webkitSupportsFullscreen) return;
  if (video.webkitDisplayingFullscreen) {
    video.webkitExitFullscreen?.();
  } else {
    video.webkitEnterFullscreen?.();
  }
}

export async function toggleElementFullscreen(element: HTMLElement): Promise<void> {
  if (typeof document === 'undefined') return;

  try {
    if (document.fullscreenEnabled) {
      await toggleStandardFullscreen(element);
      return;
    }

    const webkitVideo = resolveWebkitVideo(element);
    if (webkitVideo) {
      toggleWebkitVideoFullscreen(webkitVideo);
    }
  } catch {
    /* Fullscreen requires a user gesture; ignore benign failures. */
  }
}
